import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export function SystemMetrics() {
  const [cpu, setCpu] = useState(12.4);
  const [threads, setThreads] = useState(2048);
  const [reach, setReach] = useState(14.8);
  const [uptime, setUptime] = useState(345600);

  useEffect(() => {
    const interval = setInterval(() => {
      setCpu(prev => Math.max(5, Math.min(95, prev + (Math.random() * 4 - 2))));
      setThreads(prev => Math.floor(prev + (Math.random() * 10 - 5)));
      setReach(prev => prev + 0.001);
    }, 1500);
    const uptimeInterval = setInterval(() => setUptime(u => u + 1), 1000);
    return () => { clearInterval(interval); clearInterval(uptimeInterval); };
  }, []);

  const formatUptime = (sec: number) => {
    const d = Math.floor(sec / (3600 * 24));
    const h = Math.floor(sec % (3600 * 24) / 3600);
    const m = Math.floor(sec % 3600 / 60);
    const s = Math.floor(sec % 60);
    return `${d}d ${h}h ${m}m ${s} s`;
  };

  return (
    <div className="w-full border-y border-white/5 bg-black py-8 px-6 font-mono text-[10px] md:text-xs tracking-widest uppercase relative z-30">
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-x-0 md:divide-x divide-white/10">
        <div className="flex flex-col items-center text-center">
          <span className="text-brand/80 mb-2">Core Usage</span>
          <span className="text-white text-lg md:text-xl font-light">{cpu.toFixed(1)}%</span>
        </div>
        <div className="flex flex-col items-center text-center md:border-l border-white/10">
          <span className="text-brand/80 mb-2">Active Threads</span>
          <span className="text-white text-lg md:text-xl font-light">{threads.toLocaleString()}</span>
        </div>
        <div className="flex flex-col items-center text-center md:border-l border-white/10">
          <span className="text-brand/80 mb-2">Global Reach</span>
          <span className="text-white text-lg md:text-xl font-light">{reach.toFixed(3)}M</span>
        </div>
        <div className="flex flex-col items-center text-center md:border-l border-white/10">
          <span className="text-brand/80 mb-2">System Uptime</span>
          <span className="text-white text-lg md:text-xl font-light">{formatUptime(uptime)}</span>
        </div>
      </div>
    </div>
  );
}

export function AutonomyStream() {
  return (
    <div className="w-full bg-brand overflow-hidden py-3 border-y border-white/10 relative z-20 flex items-center">
      <motion.div
        animate={{ x: [0, -1000] }}
        transition={{ repeat: Infinity, duration: 25, ease: "linear" }}
        className="flex whitespace-nowrap font-mono text-black text-[10px] md:text-xs font-bold uppercase tracking-[0.2em]"
      >
        <span className="mx-4">&gt; SCALING NEURALWORKS INFRASTRUCTURE...</span>
        <span className="mx-4">&gt; ANALYZING 4,203 COMMENTS: 98% POSITIVE SENTIMENT...</span>
        <span className="mx-4">&gt; GENERATING 15 NEW HIGH-END ASSETS IN FORGE...</span>
        <span className="mx-4">&gt; DEPLOYING SOCIAL STRATEGY v3.0...</span>
        <span className="mx-4">&gt; NEGOTIATING B2B PARTNERSHIPS...</span>
        {/* Duplicate for infinite seamless scroll */}
        <span className="mx-4">&gt; SCALING NEURALWORKS INFRASTRUCTURE...</span>
        <span className="mx-4">&gt; ANALYZING 4,203 COMMENTS: 98% POSITIVE SENTIMENT...</span>
        <span className="mx-4">&gt; GENERATING 15 NEW HIGH-END ASSETS IN FORGE...</span>
        <span className="mx-4">&gt; DEPLOYING SOCIAL STRATEGY v3.0...</span>
        <span className="mx-4">&gt; NEGOTIATING B2B PARTNERSHIPS...</span>
      </motion.div>
    </div>
  );
}

export function TechStackMarquee() {
  const techs = [
    {
      name: "Next.js",
      desc: "Das Fundament. Erlaubt uns, blitzschnell Updates weltweit auszurollen, ohne Wartezeiten für die Nutzer.",
      color: "text-white",
      logo: <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-8 md:h-8" stroke="currentColor" strokeWidth="2"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" /><path d="M15 12L9 6v12l8.48-8.48" /></svg>
    },
    {
      name: "React",
      desc: "Die Bedienung. Sorgt dafür, dass sich unsere Plattform wie eine echte App anfühlt – extrem flüssig und direkt.",
      color: "text-[#61DAFB]",
      logo: <svg viewBox="-11.5 -10.232 23 20.463" className="w-5 h-5 md:w-8 md:h-8 fill-current"><circle cx="0" cy="0" r="2.05" fill="#61dafb" /><g stroke="#61dafb" strokeWidth="1" fill="none"><ellipse rx="11" ry="4.2" /><ellipse rx="11" ry="4.2" transform="rotate(60)" /><ellipse rx="11" ry="4.2" transform="rotate(120)" /></g></svg>
    },
    {
      name: "Swift",
      desc: "Premium Apple-Erlebnis. Wir liefern absolute Höchstleistung, nativ optimiert für alle iPhones und iPads.",
      color: "text-[#F05138]",
      logo: <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-8 md:h-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 18s-7-2.5-7-8V5l7-3 7 3v5c0 5.5-7 8-7 8z" /></svg>
    },
    {
      name: "Kotlin",
      desc: "Android Marktführerschaft. Stabile, reibungslose Systeme, die auf Milliarden von Smartphones weltweit fehlerfrei laufen.",
      color: "text-[#7F52FF]",
      logo: <svg viewBox="0 0 24 24" className="w-5 h-5 md:w-8 md:h-8 fill-current"><path d="M24 24H0V0h24L12 12Z" /></svg>
    },
    {
      name: "Supabase",
      desc: "Das unsichtbare Gedächtnis. Speichert sicher und in Lichtgeschwindigkeit alle Kunden-Aktionen rund um den Globus.",
      color: "text-[#3ECF8E]",
      logo: <svg viewBox="0 0 24 24" className="w-5 h-5 md:w-8 md:h-8 fill-current"><path d="M12 2.2L2.2 12 12 21.8l9.8-9.8L12 2.2zm0 3.8l6 6-6 6-6-6 6-6z" /></svg>
    },
    {
      name: "Vercel",
      desc: "Die globale Schutzschild. Server-Infrastruktur, die garantiert, dass wir selbst bei Millionenanstürmen nie offline gehen.",
      color: "text-white",
      logo: <svg viewBox="0 0 24 24" className="w-5 h-5 md:w-8 md:h-8 fill-current"><path d="M12 2L2 22h20L12 2z" /></svg>
    },
    {
      name: "Gemini AI",
      desc: "Das digitale Gehirn. Versteht komplexe Nutzer-Wünsche, analysiert Trends und steuert unsere Agenten völlig autonom.",
      color: "text-[#8E75FF]",
      logo: <svg viewBox="0 0 24 24" className="w-5 h-5 md:w-8 md:h-8 fill-current"><path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4L12 2z" /></svg>
    },
    {
      name: "ComfyUI",
      desc: "Die visuelle Fabrik. Produziert High-End Fotokampagnen und Designs, massenhaft und absolut vollautomatisiert.",
      color: "text-[#FF0055]",
      logo: <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-8 md:h-8" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M10 6.5h4M10 17.5h4M6.5 10v4M17.5 10v4" /></svg>
    },
    {
      name: "RunPod",
      desc: "Brute-Force Rechenzentren. Massive Grafikpower aus der Cloud, um jede noch so schwere Aufgabe in Sekunden zu erledigen.",
      color: "text-[#673AB7]",
      logo: <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-8 md:h-8" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" ry="2" /><path d="M6 8h.01M6 12h.01M6 16h.01M10 8h8M10 12h8M10 16h8" /></svg>
    },
    {
      name: "Python",
      desc: "Der smarte Dirigent. Überwacht im Hintergrund gewaltige Datenmassen und verbindet unsere KI mit der echten Welt.",
      color: "text-[#FFD43B]",
      logo: <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-8 md:h-8" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
    }
  ];

  return (
    <div className="w-full bg-[#050505] py-10 md:py-14 border-y border-white/5 overflow-hidden flex flex-col justify-center relative z-20 group/marquee">
      <style>
        {`
          @keyframes infinite-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.333333%); }
          }
          .animate-infinite-scroll {
            animation: infinite-scroll 45s linear infinite;
            width: max-content;
          }
          .group\\/marquee:hover .animate-infinite-scroll,
          .group\\/marquee:active .animate-infinite-scroll {
            animation-play-state: paused;
          }
        `}
      </style>

      <div className="absolute left-0 top-0 bottom-0 w-8 md:w-32 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-8 md:w-32 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none"></div>

      <div className="animate-infinite-scroll flex items-start md:cursor-pointer pb-6">
        {[...techs, ...techs, ...techs].map((tech, i) => (
          <div key={i} className="flex items-start gap-4 mx-6 md:mx-10 group/item min-w-[300px] md:min-w-[400px] max-w-[300px] md:max-w-[400px]">
            <div className={`mt-1 transition-transform duration-500 group-hover/item:scale-125 group-hover/item:-rotate-12 shrink-0 ${tech.color} drop-shadow-[0_0_8px_currentColor]`}>
              {tech.logo}
            </div>
            <div className="flex flex-col w-full">
              <span className={`font-mono text-base md:text-2xl font-bold uppercase tracking-wider transition-all duration-300 group-hover/item:drop-shadow-[0_0_15px_currentColor] ${tech.color} whitespace-normal leading-tight md:leading-normal`}>
                {tech.name}
              </span>
              <div className="grid grid-rows-[1fr] md:grid-rows-[0fr] md:group-hover/item:grid-rows-[1fr] transition-all duration-500 ease-in-out">
                <div className="overflow-hidden">
                  <p className="font-sans text-[11px] md:text-sm text-neutral-400 mt-2 md:mt-3 leading-relaxed opacity-100 md:opacity-0 md:group-hover/item:opacity-100 transition-opacity duration-300 delay-100 pr-2 pb-2">
                    {tech.desc}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                        7. CONVERSATIONAL CONTACT                           */
/* -------------------------------------------------------------------------- */
